import React, { useState, useRef, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import { getJobTypeLabel } from '../../../utils/jobTypeUtils';
import { getSafeSkillName } from '../../../utils/skillsUtils';
import { formatHourlySalaryRange } from '../../../utils/currencyUtils';
import ImageWithFallback from 'components/ImageWithFallback';
import { calculateMatchScore } from '../../../services/candidate-service';

const JobCard = ({ job, onViewDetails, onApply, onBookmark, onContactEmployer, userProfile }) => {
  const [isBookmarked, setIsBookmarked] = useState(job?.isBookmarked || false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  // Intersection Observer for lazy rendering
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect(); // Stop observing once visible
          }
        });
      },
      {
        rootMargin: '100px', // Load 100px before card comes into view
        threshold: 0.01
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  const handleBookmark = () => {
    const newBookmarkState = !isBookmarked;
    setIsBookmarked(newBookmarkState);
    if (onBookmark) {
      onBookmark(job?.id, newBookmarkState);
    }
  };

  const formatSalary = (hourlyMin, hourlyMax, jobType, jobCurrency = 'USD') => {
    // Get user's preferred currency from profile (fallback to USD)
    const userCurrency = userProfile?.preferred_currency || 'USD';
    
    return formatHourlySalaryRange({
      hourlyMin,
      hourlyMax,
      jobType,
      jobCurrency,
      userCurrency,
      showConversionNote: false // Don't show conversion note in cards to save space
    }) || 'TBD';
  };

  const getExperienceLabel = (level) => {
    const labels = {
      'entry': 'Entry Level',
      'intermediate': 'Intermediate',
      'senior': 'Senior',
      'expert': 'Expert'
    };
    return labels[level] || level;
  };

  const calculateSkillMatch = () => {
    // First, check if we have a pre-calculated matchScore from the database
    if (job?.matchScore !== undefined && job?.matchScore > 0) {
      return job.matchScore;
    }

    // Use the unified calculateMatchScore function for consistency with recommended jobs
    if (!userProfile?.skills) {
      return 0;
    }

    // Prepare candidate object with normalized skills (don't lowercase here, let calculateMatchScore handle it)
    const candidate = {
      skills: userProfile.skills.map(skill => getSafeSkillName(skill)),
      experience_years: userProfile?.experience_years || 0
    };

    // Prepare job object with normalized skills (don't lowercase here, let calculateMatchScore handle it)
    const jobData = {
      skills: (job?.skills_required || job?.skills || []).map(skill => {
        return typeof skill === 'string' ? skill : skill?.name || '';
      }).filter(s => s) // Remove empty strings
    };

    // Check if there's at least one matching skill
    const hasMatchingSkill = jobData.skills.length > 0 && 
      jobData.skills.some(jobSkill => 
        candidate.skills.some(candidateSkill => 
          candidateSkill.toLowerCase() === jobSkill.toLowerCase()
        )
      );

    // Only calculate match score if there's at least one matching skill
    if (!hasMatchingSkill) {
      return 0;
    }

    // Return unified match score calculation
    return calculateMatchScore(candidate, jobData);
  };

  const skillMatch = calculateSkillMatch();

  const getMatchColor = (percentage) => {
    if (percentage >= 80) return 'text-success bg-success-100';
    if (percentage >= 60) return 'text-warning bg-warning-100';
    if (percentage >= 40) return 'text-accent bg-accent-100';
    return 'text-text-muted bg-secondary-100';
  };

  // Show skeleton while not visible
  if (!isVisible) {
    return (
      <div 
        ref={cardRef}
        className="bg-surface rounded-lg border border-border p-6 animate-pulse"
      >
        <div className="h-32 bg-secondary-200 rounded"></div>
      </div>
    );
  }

  return (
    <div 
      ref={cardRef}
      className="bg-surface flex justify-between flex-col rounded-lg border border-border p-6 hover:shadow-md transition-all duration-200 group cursor-pointer"
      onClick={() => onViewDetails(job)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          {(job?.employer?.company_logo || job?.employer?.profile_picture) && (
            <div className="w-12 h-12 rounded-lg overflow-hidden bg-secondary-100 flex-shrink-0">
              <ImageWithFallback
                src={job?.employer?.company_logo || job?.employer?.profile_picture}
                fallbackSrc="assets/images/Logo/webp/VAS-LOGO.webp"
                alt={job?.employer?.company_name || 'Company'}
                className="w-full h-full object-contain"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-text-primary text-lg line-clamp-1 group-hover:text-primary transition-colors">
              {job?.title}
            </h3>
            <p className="text-text-secondary text-sm">
              {job?.company_name || job?.employer?.company_name || job?.employer?.full_name || 'Unknown Company'}
            </p>
            <div className="flex items-center space-x-4 mt-1">
              <div className="flex items-center space-x-1">
                <Icon name="MapPin" size={14} className="text-text-muted" />
                <span className="text-xs text-text-muted">{job?.location}</span>
              </div>
              {job?.is_remote && (
                <span className="bg-accent-100 text-accent-700 text-xs px-2 py-0.5 rounded-full">
                  Remote
                </span>
              )}
            </div>
          </div>
        </div>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleBookmark();
          }}
          className="p-2 rounded-lg hover:bg-secondary-50 transition-colors duration-200 flex-shrink-0"
        >
          <Icon 
            name={isBookmarked ? "Bookmark" : "BookmarkPlus"} 
            size={18} 
            className={isBookmarked ? "text-warning" : "text-text-muted hover:text-warning"} 
          />
        </button>
      </div>

      {/* Skills Match (for authenticated users) */}
      {userProfile && skillMatch > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-text-secondary">Match Score</span>
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${getMatchColor(skillMatch)}`}>
              {skillMatch}%
            </span>
          </div>
          {/* <div className="w-full bg-secondary-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                skillMatch >= 80 ? 'bg-success' :
                skillMatch >= 60 ? 'bg-warning' :
                skillMatch >= 40 ? 'bg-accent' : 'bg-text-muted'
              }`}
              style={{ width: `${skillMatch}%` }}
            ></div>
          </div> */}
        </div>
      )}

      {/* Description */}
      <p className="text-sm text-text-secondary line-clamp-2 mb-4">
        {job?.short_description || job?.description}
      </p>

      {/* Job Details */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1">
            <Icon name="DollarSign" size={16} className="text-success" />
            <span className="text-sm font-medium text-text-primary">
              {formatSalary(job?.budget_min, job?.budget_max, job?.job_type, job?.currency)}
            </span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Briefcase" size={16} className="text-text-muted" />
            <span className="text-sm text-text-secondary">
              {getJobTypeLabel(job?.job_type)}
            </span>
          </div>
        </div>
        
        <span className="text-xs text-text-muted bg-secondary-100 px-2 py-1 rounded-full">
          {getExperienceLabel(job?.experience_level)}
        </span>
      </div>

      {/* Skills Tags */}
      <div className="flex flex-wrap gap-2 mb-4">
        {(job?.skills_required || job?.required_skills)?.slice(0, 3).map((skill, index) => (
          <span 
            key={index}
            className="px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-md"
          >
            {getSafeSkillName(skill)}
          </span>
        ))}
        {(job?.skills_required || job?.required_skills)?.length > 3 && (
          <span className="px-2 py-1 bg-secondary-100 text-text-secondary text-xs rounded-md">
            +{(job.skills_required || job.required_skills).length - 3} more
          </span>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-4 text-xs text-text-muted">
          <div className="flex items-center space-x-1">
            <Icon name="Clock" size={12} />
            <span>
              {job?.created_at 
                ? formatDistanceToNow(new Date(job.created_at), { addSuffix: true })
                : 'Recently posted'
              }
            </span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Only show Apply button for VAs on active, non-expired jobs */}
          {userProfile?.role !== 'employer' &&
           job?.status !== 'expired' &&
           job?.status !== 'filled' &&
           !(job?.application_deadline && new Date(job.application_deadline) < new Date()) && (
            <Button
              variant={job?.isApplied ? "secondary" : "primary"}
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                if (!job?.isApplied) {
                  onApply(job);
                }
              }}
              disabled={job?.isApplied}
              className="text-xs"
              iconName={job?.isApplied ? "Check" : "Send"}
              iconSize={14}
            >
              {job?.isApplied ? "Applied" : "Apply"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobCard;
